import React, { useState, useEffect, useCallback } from "react";
import { Send, Loader2, RefreshCw, Printer, Download, Upload, Star, Trash2, BookMarked, Pencil, Plus, ChevronDown, Landmark, Check } from "lucide-react";
import { toast } from "sonner";
import { CounterpartyAutocomplete } from "./CounterpartyAutocomplete";

const fmtTs = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const isValidFax = (f) => (f || "").replace(/[^\d]/g, "").length >= 10;

// After picking a counterparty from FDIC/search (or right after faxing one),
// offer to remember it in the rolodex — including its routing / SWIFT so the
// directory fills itself over time.
const SaveToRolodex = ({ client, picked, fax, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [routing, setRouting] = useState(picked?.routing || "");
  const [swift, setSwift] = useState(picked?.swift || "");
  useEffect(() => { setRouting(picked?.routing || ""); setSwift(picked?.swift || ""); }, [picked]);
  if (!picked || picked.source === "rolodex") return null;
  const effFax = (fax || picked.fax || "").trim();
  const save = async () => {
    if (!isValidFax(effFax)) { toast.error("Enter the fax number first, then save to rolodex."); return; }
    setSaving(true);
    try {
      await client.post("/admin/fax/directory", {
        category: picked.category && picked.category !== "FDIC-Insured Bank" ? picked.category : "Banks & Lenders",
        label: picked.name || effFax, fax: effFax, address: picked.address || "",
        routing: routing.trim(), swift: swift.trim(), account: picked.account || "",
        confirmed: false,
      });
      setDone(true);
      toast.success(`Saved ${picked.name || effFax} to your rolodex.`);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };
  if (done) return (
    <div className="mb-3 flex items-center gap-2 text-[11px] text-[#5fd3a8]" data-testid="fax-save-rolodex-done">
      <Check size={13} /> Saved <span className="text-[#C9C9C0]">{picked.name}</span> to your rolodex.
    </div>
  );
  return (
    <div className="mb-3 rounded-md border border-[#B8860B]/30 bg-[#B8860B]/5 px-2.5 py-2 space-y-2" data-testid="fax-save-rolodex">
      <div className="flex items-center gap-2">
        <Landmark size={13} className="text-[#3DBB8E] shrink-0" />
        <span className="flex-1 text-[11px] text-[#C9C9C0] truncate">
          Remember <span className="text-[#E4C97A] font-medium">{picked.name || effFax}</span>
          {isValidFax(effFax) ? "" : <span className="text-[#C9A227]"> — add a fax number to save</span>} for next time?
        </span>
        <button type="button" onClick={save} disabled={saving || !isValidFax(effFax)} data-testid="fax-save-rolodex-btn"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/15 disabled:opacity-40 rounded px-2 py-1 whitespace-nowrap">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <BookMarked size={11} />} Save to rolodex
        </button>
      </div>
      <div className="flex items-center gap-2 pl-[21px]">
        <input value={routing} onChange={(e) => setRouting(e.target.value)} placeholder="ABA routing (optional)" data-testid="fax-save-rolodex-routing"
          className="flex-1 bg-white text-black border border-[#D8D8D0] rounded px-2 py-1 text-[11px]" />
        <input value={swift} onChange={(e) => setSwift(e.target.value)} placeholder="SWIFT / BIC (optional)" data-testid="fax-save-rolodex-swift"
          className="flex-1 bg-white text-black border border-[#D8D8D0] rounded px-2 py-1 text-[11px]" />
      </div>
    </div>
  );
};

// Shared "fax book" picker — select a saved counterparty fax number or save the
// current one. Every eFax that is sent is also auto-saved as a preset.
export const FaxPresetPicker = ({ client, value, onChange, testidPrefix = "fax-preset", reloadToken = 0 }) => {
  const [presets, setPresets] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [manage, setManage] = useState(false);
  const load = useCallback(async () => {
    try { const r = await client.get("/admin/fax/presets"); setPresets(r.data.presets || []); } catch { /* noop */ }
  }, [client]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => { try { const r = await client.get("/admin/fax/directory"); setDirectory(r.data.directory || []); } catch { /* noop */ } })();
  }, [client, reloadToken]);

  const saveCurrent = async () => {
    const fax = (value || "").trim();
    if (!fax) { toast.error("Enter a fax number first."); return; }
    const label = window.prompt("Name this counterparty (optional):", "") || undefined;
    try { await client.post("/admin/fax/presets", { fax, label }); toast.success("Saved to fax book."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };
  const remove = async (id) => {
    try { await client.delete(`/admin/fax/presets/${id}`); load(); } catch { /* noop */ }
  };

  return (
    <div className="mb-3" data-testid={`${testidPrefix}-wrap`}>
      <div className="flex items-center gap-2">
        <select
          value=""
          onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
          data-testid={`${testidPrefix}-select`}
          className="flex-1 bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[12px]">
          <option value="">{presets.length ? "★ Pick a saved counterparty…" : "★ Pick from directory / save one…"}</option>
          {presets.length > 0 && (
            <optgroup label="★ Your saved counterparties">
              {presets.map((p) => (
                <option key={p.id} value={p.fax}>{p.label && p.label !== p.fax ? `${p.label} · ${p.fax}` : p.fax}</option>
              ))}
            </optgroup>
          )}
          {directory.map((grp) => (
            <optgroup key={grp.category} label={`📇 ${grp.category}`}>
              {grp.entries.map((e) => (
                <option key={e.id || e.fax} value={e.fax}>{`${e.confirmed ? "✓ " : "• "}${e.label} · ${e.fax}`}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <button type="button" onClick={saveCurrent} data-testid={`${testidPrefix}-save-btn`}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] border border-[#B8860B]/40 text-[#E4C97A] hover:bg-[#B8860B]/10 rounded-md px-2.5 py-2 whitespace-nowrap">
          <Star size={12} /> Save
        </button>
        {presets.length > 0 && (
          <button type="button" onClick={() => setManage((m) => !m)} data-testid={`${testidPrefix}-manage-btn`}
            title="Manage saved counterparties"
            className="inline-flex items-center text-[11px] text-[#8A8A8A] hover:text-[#ECECE4] border border-[#2A2A22] rounded-md px-2 py-2">
            <BookMarked size={12} />
          </button>
        )}
      </div>
      {directory.length > 0 && (
        <div className="mt-1 text-[10px] text-[#7A7A70]" data-testid={`${testidPrefix}-note`}>
          📇 Directory: <span className="text-[#5fd3a8]">✓ confirmed</span> · <span className="text-[#C9A227]">• verify the number before transmitting</span>.
        </div>
      )}
      {manage && presets.length > 0 && (
        <div className="mt-2 border border-[#2A2A22] rounded-md divide-y divide-[#1F1F18]" data-testid={`${testidPrefix}-manage-list`}>
          {presets.map((p) => (
            <div key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]" data-testid={`${testidPrefix}-row-${p.id}`}>
              <button type="button" onClick={() => onChange(p.fax)} className="flex-1 text-left text-[#C9C9C0] hover:text-[#E4C97A] truncate">
                {p.label && p.label !== p.fax ? `${p.label} · ` : ""}<span className="font-mono">{p.fax}</span>
                {p.use_count > 1 ? <span className="text-[#5C5C5C]"> · used {p.use_count}×</span> : null}
              </button>
              <button type="button" onClick={() => remove(p.id)} data-testid={`${testidPrefix}-del-${p.id}`}
                className="text-[#FF8585] hover:text-[#ff6060]"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Editable rolodex — add/edit/delete counterparty fax lines and mark them confirmed.
export const DirectoryManager = ({ client, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ category: "Mortgage Servicers", label: "", fax: "", address: "", routing: "", swift: "", account: "", tint: "", confirmed: false });
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState("");
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    try { const r = await client.get("/admin/fax/directory"); setGroups(r.data.directory || []); } catch { /* noop */ }
  }, [client]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const refresh = async () => { await load(); onChanged && onChanged(); };

  const add = async () => {
    if (!form.label.trim() || (!form.fax.trim() && !form.routing.trim())) { toast.error("Enter a name and a fax or routing number."); return; }
    setBusy(true);
    try { await client.post("/admin/fax/directory", form); toast.success("Added to directory."); setForm({ ...form, label: "", fax: "", address: "", routing: "", swift: "", account: "" }); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
    finally { setBusy(false); }
  };
  const importCsv = async () => {
    if (!csv.trim()) { toast.error("Paste CSV first."); return; }
    setImporting(true);
    try {
      const r = await client.post("/admin/fax/directory/import", { csv });
      toast.success(`Imported ${r.data.added} · skipped ${r.data.skipped}`);
      setCsv(""); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed."); }
    finally { setImporting(false); }
  };
  const toggleConfirmed = async (e) => {
    try { await client.put(`/admin/fax/directory/${e.id}`, { confirmed: !e.confirmed }); await refresh(); } catch { toast.error("Update failed."); }
  };
  const edit = async (e) => {
    const label = window.prompt("Name:", e.label); if (label === null) return;
    const fax = window.prompt("Fax number (E.164, e.g. +18005551234):", e.fax); if (fax === null) return;
    const category = window.prompt("Category:", e.category) ?? e.category;
    try { await client.put(`/admin/fax/directory/${e.id}`, { label, fax, category }); await refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Edit failed."); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.label}?`)) return;
    try { await client.delete(`/admin/fax/directory/${e.id}`); await refresh(); } catch { toast.error("Delete failed."); }
  };

  const uniqueCats = [...new Set(["Mortgage Servicers", "Banks & Lenders", "Treasury & Agencies", "Title & Escrow", "Other", ...groups.map((g) => g.category)])];

  return (
    <div className="mt-4 border border-[#2A2A22] rounded-md" data-testid="fax-directory-manager">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="fax-directory-toggle"
        className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] text-[#C9C9C0] hover:text-[#ECECE4]">
        <BookMarked size={14} className="text-[#C9A227]" />
        <span className="flex-1 text-left font-semibold">Manage counterparty directory (rolodex)</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#2A2A22] p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-[#0F0F0A] p-2.5 rounded-md">
            <div className="md:col-span-12">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Look up a bank / servicer (FDIC + your rolodex)</label>
              <CounterpartyAutocomplete
                testid="fax-directory-fdic"
                placeholder="Search FDIC banks & your rolodex to auto-fill…"
                onSelect={(r) => setForm((f) => ({
                  ...f,
                  label: r.name || f.label,
                  address: r.address || f.address,
                  fax: r.fax || f.fax,
                  routing: r.routing || f.routing,
                  swift: r.swift || f.swift,
                  account: r.account || f.account,
                  category: r.source === "fdic" ? "Banks & Lenders" : (r.category || f.category),
                }))}
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="fax-directory-add-category"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]">
                {uniqueCats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Name / Department</label>
              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Rocket Mortgage — Payoff" data-testid="fax-directory-add-label"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Fax (E.164)</label>
              <input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} placeholder="+18005551234" data-testid="fax-directory-add-fax"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]" />
            </div>
            <div className="md:col-span-12">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Address (optional)</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Payoff / mailing address" data-testid="fax-directory-add-address"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]" />
            </div>
            <div className="md:col-span-4">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">ABA Routing (public, 9-digit)</label>
              <input value={form.routing} onChange={(e) => setForm({ ...form, routing: e.target.value })} placeholder="e.g. 021000021" data-testid="fax-directory-add-routing"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]" />
            </div>
            <div className="md:col-span-4">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">SWIFT / BIC (optional)</label>
              <input value={form.swift} onChange={(e) => setForm({ ...form, swift: e.target.value })} placeholder="e.g. CHASUS33" data-testid="fax-directory-add-swift"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]" />
            </div>
            <div className="md:col-span-4">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Default Account / Reference (optional)</label>
              <input value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} placeholder="Institutional payoff account" data-testid="fax-directory-add-account"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]" />
            </div>
            <div className="md:col-span-4">
              <label className="block text-[9px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Draft color (default for this counterparty)</label>
              <select value={form.tint} onChange={(e) => setForm({ ...form, tint: e.target.value })} data-testid="fax-directory-add-tint"
                className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px]">
                <option value="">— none —</option>
                <option value="blue">Blue safety paper</option>
                <option value="green">Green safety paper</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-1 text-[10px] text-[#C9C9C0] mb-1">
                <input type="checkbox" checked={form.confirmed} onChange={(e) => setForm({ ...form, confirmed: e.target.checked })} data-testid="fax-directory-add-confirmed" /> Confirmed
              </label>
              <button type="button" onClick={add} disabled={busy} data-testid="fax-directory-add-btn"
                className="w-full inline-flex items-center justify-center gap-1 text-[11px] bg-[#0B6B4F]/20 border border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 rounded px-2 py-1.5">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
              </button>
            </div>
          </div>

          <div className="border border-[#2A2A22] rounded-md">
            <button type="button" onClick={() => setShowImport((s) => !s)} data-testid="fax-directory-import-toggle"
              className="w-full flex items-center gap-2 px-2.5 py-2 text-[11px] text-[#C9C9C0] hover:text-[#ECECE4]">
              <Plus size={12} className="text-[#3DBB8E]" /> Bulk import from CSV
              <ChevronDown size={13} className={`ml-auto transition-transform ${showImport ? "rotate-180" : ""}`} />
            </button>
            {showImport && (
              <div className="px-2.5 pb-2.5 space-y-2">
                <div className="text-[10px] text-[#8A8A8A]">Columns: <span className="font-mono">name, address, fax, category, confirmed</span> (header row optional).</div>
                <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} data-testid="fax-directory-import-csv"
                  placeholder={"name,address,fax,category,confirmed\nRocket Mortgage — Payoff,1050 Woodward Ave Detroit MI,+13135551234,Mortgage Servicers,yes"}
                  className="w-full bg-white text-black border border-[#D8D8D0] rounded px-2 py-1.5 text-[11px] font-mono" />
                <button type="button" onClick={importCsv} disabled={importing} data-testid="fax-directory-import-btn"
                  className="inline-flex items-center gap-1 text-[11px] bg-[#0B6B4F]/20 border border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 rounded px-3 py-1.5">
                  {importing ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Import CSV
                </button>
              </div>
            )}
          </div>

          {groups.map((grp) => (
            <div key={grp.category} data-testid={`fax-directory-group-${grp.category}`}>
              <div className="text-[9px] uppercase tracking-[0.16em] text-[#8A6D1B] font-bold mb-1">{grp.category}</div>
              <div className="border border-[#2A2A22] rounded-md divide-y divide-[#1F1F18]">
                {grp.entries.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]" data-testid={`fax-directory-row-${e.id}`}>
                    <button type="button" onClick={() => toggleConfirmed(e)} title="Toggle confirmed" data-testid={`fax-directory-confirm-${e.id}`}
                      className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${e.confirmed ? "text-[#5fd3a8] border-[#0B6B4F]/50" : "text-[#C9A227] border-[#B8860B]/40"}`}>
                      {e.confirmed ? "✓ Confirmed" : "• Verify"}
                    </button>
                    <span className="flex-1 truncate text-[#C9C9C0]">{e.label} · <span className="font-mono">{e.fax}</span>{e.routing ? <span className="text-[#8A6D1B]"> · ABA {e.routing}</span> : null}{e.address ? <span className="text-[#6C6C64]"> · {e.address}</span> : null}</span>
                    <button type="button" onClick={() => edit(e)} data-testid={`fax-directory-edit-${e.id}`} className="text-[#8A8A8A] hover:text-[#ECECE4]"><Pencil size={12} /></button>
                    <button type="button" onClick={() => remove(e)} data-testid={`fax-directory-del-${e.id}`} className="text-[#FF8585] hover:text-[#ff6060]"><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const statusColor = (s) => {
  if (["delivered"].includes(s)) return "bg-[#0B6B4F]/20 text-[#3DBB8E] border-[#0B6B4F]/40";
  if (["failed"].includes(s)) return "bg-[#9E2A2B]/15 text-[#FF8585] border-[#9E2A2B]/40";
  return "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40";
};

export const FaxPanel = ({ client, focusTx }) => {
  const [txs, setTxs] = useState([]);
  const [txId, setTxId] = useState("");
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState({});
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState("");
  const [jobs, setJobs] = useState([]);
  const [dirVersion, setDirVersion] = useState(0);
  const [pickedCp, setPickedCp] = useState(null);
  const [pickedUp, setPickedUp] = useState(null);
  const [savePrompt, setSavePrompt] = useState(null);
  const bumpDir = () => setDirVersion((v) => v + 1);

  const loadJobs = useCallback(async () => {
    try { const r = await client.get("/admin/fax/jobs"); setJobs(r.data.jobs || []); } catch { /* noop */ }
  }, [client]);

  const downloadCert = useCallback(async (id) => {
    const win = window.open("", "_blank");
    try {
      const r = await client.get(`/admin/fax/certificate/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (win && !win.closed) {
        win.location = url;
      } else {
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win && !win.closed) win.close();
      toast.error("Could not generate certificate.");
    }
  }, [client]);

  const pickDeal = useCallback(async (id) => {
    setTxId(id); setDocs([]); setSelected({}); setMsg("");
    if (!id) return;
    try {
      const r = await client.get(`/admin/fax/documents/${id}`);
      setDocs(r.data.documents || []);
      setSelected(Object.fromEntries((r.data.documents || []).map((d) => [d, true])));
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not load documents."); }
  }, [client]);

  useEffect(() => {
    client.get("/transactions").then((r) => setTxs(r.data || [])).catch(() => {});
    loadJobs();
    const t = setInterval(loadJobs, 15000);
    return () => clearInterval(t);
  }, [client, loadJobs]);

  // Deep-link from a borrower/broker row: preselect the deal and scroll here.
  useEffect(() => {
    if (focusTx?.txId) {
      pickDeal(focusTx.txId);
      document.getElementById("sec-fax")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusTx, pickDeal]);

  const send = async () => {
    setMsg("");
    const doc_keys = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!txId) { setMsg("Pick a deal."); return; }
    if (!to.trim()) { setMsg("Enter the counterparty fax number."); return; }
    if (doc_keys.length === 0) { setMsg("Select at least one document."); return; }
    setSending(true);
    try {
      const r = await client.post(`/admin/fax/send/${txId}`, { to, doc_keys });
      setMsg(`✓ Fax queued (id ${r.data.fax_id}).`);
      setSavePrompt({ fax: to, name: pickedCp?.name || "", source: pickedCp?.source, address: pickedCp?.address || "", routing: pickedCp?.routing || "", swift: pickedCp?.swift || "", account: pickedCp?.account || "", category: pickedCp?.category });
      setPickedCp(null);
      loadJobs();
    } catch (e) { setMsg("⚠ " + (e?.response?.data?.detail || "Fax failed.")); }
    setSending(false);
  };

  const downloadPackage = async () => {
    setMsg("");
    const doc_keys = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!txId) { setMsg("Pick a deal."); return; }
    setDownloading(true);
    try {
      const r = await client.post(`/admin/fax/package/${txId}`, { to: to || "+10000000000", doc_keys }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `fax_package_${txId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setMsg("✓ Package downloaded (completed Private Negotiable Instrument + selected documents).");
    } catch (e) { setMsg("⚠ " + (e?.response?.data?.detail || "Could not build package.")); }
    setDownloading(false);
  };

  const [upFile, setUpFile] = useState(null);
  const [upTo, setUpTo] = useState("");
  const [uploading, setUploading] = useState(false);
  const [upMsg, setUpMsg] = useState("");
  const uploadFax = async () => {
    setUpMsg("");
    if (!upFile) { setUpMsg("Choose a file to fax."); return; }
    if (!upTo.trim()) { setUpMsg("Enter the recipient fax number."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("to", upTo);
      fd.append("file", upFile);
      const r = await client.post("/admin/fax/send-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpMsg(`✓ Fax queued (id ${r.data.fax_id}) — ${r.data.filename}.`);
      setUpFile(null);
      setSavePrompt({ fax: upTo, name: pickedUp?.name || "", source: pickedUp?.source, address: pickedUp?.address || "", routing: pickedUp?.routing || "", swift: pickedUp?.swift || "", account: pickedUp?.account || "", category: pickedUp?.category });
      setPickedUp(null);
      loadJobs();
    } catch (e) { setUpMsg("⚠ " + (e?.response?.data?.detail || "Fax failed.")); }
    setUploading(false);
  };

  const dealLabel = (t) => `${t.trust_name || t.counterparty_name || t.id.slice(0, 8)} · ${t.path} · $${Number(t.face_value || 0).toLocaleString()}`;

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden" data-testid="fax-panel">
      <div className="p-5 border-b border-[#2A2A22] bg-[#17170F]" data-testid="fax-upload-card">
        <div className="flex items-center gap-2 mb-3">
          <Upload size={15} className="text-[#3DBB8E]" />
          <span className="text-[13px] text-[#ECECE4] font-semibold">Upload &amp; submit any file as a payload</span>
          <span className="text-[11px] text-[#6C6C64]">PDF, TIFF, PNG or JPG · up to 20&nbsp;MB</span>
        </div>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-1">
            <label className="block text-[11px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">File</label>
            <input type="file" accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg" data-testid="fax-upload-file"
              onChange={(e) => setUpFile(e.target.files?.[0] || null)}
              className="w-full text-[12px] text-[#C9C9C0] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#2A2A22] file:text-[#E4C97A] file:text-[11px] file:uppercase file:tracking-[0.1em]" />
          </div>
          <div className="md:col-span-1">
            <label className="block text-[11px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Recipient fax (E.164)</label>
            <div className="mb-2">
              <CounterpartyAutocomplete
                testid="fax-upload-fdic"
                placeholder="Search FDIC banks & your rolodex…"
                onSelect={(r) => { if (r.fax) setUpTo(r.fax); setPickedUp(r); }}
              />
            </div>
            <SaveToRolodex client={client} picked={pickedUp} fax={upTo} onSaved={bumpDir} />
            <FaxPresetPicker client={client} value={upTo} onChange={setUpTo} testidPrefix="fax-upload-preset" reloadToken={dirVersion} />
            <input value={upTo} onChange={(e) => setUpTo(e.target.value)} placeholder="+13125551234" data-testid="fax-upload-to"
              className="w-full bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px]" />
          </div>
          <div className="md:col-span-1">
            <button onClick={uploadFax} disabled={uploading} data-testid="fax-upload-send-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0B6B4F] hover:bg-[#0a5a43] disabled:opacity-50 text-white font-semibold text-[12px] uppercase tracking-[0.1em] px-4 py-2 rounded-md">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit this payload
            </button>
          </div>
        </div>
        {upFile && <div className="mt-2 text-[11px] text-[#8A8A8A]" data-testid="fax-upload-name">Selected: {upFile.name} ({(upFile.size / 1024).toFixed(0)} KB)</div>}
        {upMsg && <div className="mt-2 text-[12px] text-[#C9C9C0]" data-testid="fax-upload-msg">{upMsg}</div>}
      </div>
      <div className="grid md:grid-cols-2">
        <div className="p-5 border-r border-[#2A2A22]">
          <label className="block text-[11px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Deal</label>
          <select value={txId} onChange={(e) => pickDeal(e.target.value)} data-testid="fax-deal-select"
            className="w-full bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] mb-4">
            <option value="">— Select a deal —</option>
            {txs.map((t) => <option key={t.id} value={t.id}>{dealLabel(t)}</option>)}
          </select>

          <label className="block text-[11px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Counterparty fax number (E.164)</label>
          <div className="mb-2">
            <CounterpartyAutocomplete
              testid="fax-deal-fdic"
              placeholder="Search FDIC banks & your rolodex…"
              onSelect={(r) => { if (r.fax) setTo(r.fax); setPickedCp(r); }}
            />
          </div>
          <SaveToRolodex client={client} picked={pickedCp} fax={to} onSaved={bumpDir} />
          <FaxPresetPicker client={client} value={to} onChange={setTo} testidPrefix="fax-deal-preset" reloadToken={dirVersion} />
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+13125551234" data-testid="fax-to-input"
            className="w-full bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] mb-4" />
          <DirectoryManager client={client} onChanged={bumpDir} />

          <label className="block text-[11px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-2">Documents to fax</label>
          {docs.length === 0 ? (
            <div className="text-[12px] text-[#5C5C5C]">Select a deal to list its documents.</div>
          ) : (
            <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1" data-testid="fax-doc-list">
              <div className="flex gap-3 pb-1">
                <button className="text-[10px] text-[#9DB4E8]" onClick={() => setSelected(Object.fromEntries(docs.map((d) => [d, true])))}>Select all</button>
                <button className="text-[10px] text-[#8A8A8A]" onClick={() => setSelected({})}>Clear</button>
              </div>
              {docs.map((d) => (
                <label key={d} className="flex items-center gap-2 text-[12px] text-[#ECECE4] cursor-pointer">
                  <input type="checkbox" checked={!!selected[d]} data-testid={`fax-doc-${d}`}
                    onChange={(e) => setSelected((p) => ({ ...p, [d]: e.target.checked }))} />
                  {d.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          )}

          <button onClick={send} disabled={sending} data-testid="fax-send-btn"
            className="mt-4 inline-flex items-center gap-2 bg-[#0B6B4F] hover:bg-[#0a5a43] disabled:opacity-50 text-white font-semibold text-[12px] uppercase tracking-[0.1em] px-4 py-2 rounded-md">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit payload to counterparty
          </button>
          <button onClick={downloadPackage} disabled={downloading} data-testid="fax-download-btn"
            className="mt-4 ml-2 inline-flex items-center gap-2 bg-[#2A2A22] hover:bg-[#3A3A2E] disabled:opacity-50 text-[#E4C97A] font-semibold text-[12px] uppercase tracking-[0.1em] px-4 py-2 rounded-md border border-[#B8860B]/40">
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download package
          </button>
          {msg && <div className="mt-2 text-[12px] text-[#C9C9C0]" data-testid="fax-msg">{msg}</div>}
          {savePrompt && (
            <div className="mt-3" data-testid="fax-postsend-save">
              <SaveToRolodex
                key={`sent-${savePrompt.fax}`}
                client={client}
                picked={{ name: savePrompt.name, source: "sent", category: savePrompt.category || "Banks & Lenders", address: savePrompt.address, routing: savePrompt.routing, swift: savePrompt.swift, account: savePrompt.account }}
                fax={savePrompt.fax}
                onSaved={() => { bumpDir(); setSavePrompt(null); }}
              />
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Printer size={15} className="text-[#B8860B]" />
            <span className="text-[13px] text-[#ECECE4]">Recent faxes</span>
            <button onClick={loadJobs} className="ml-auto text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="fax-refresh-btn"><RefreshCw size={13} /></button>
          </div>
          {jobs.length === 0 ? (
            <div className="text-[12px] text-[#5C5C5C]">No faxes sent yet.</div>
          ) : (
            <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1" data-testid="fax-jobs">
              {jobs.map((j) => (
                <div key={j.id} className="border border-[#2A2A22] rounded-md p-2.5" data-testid={`fax-job-${j.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-[#ECECE4] truncate flex-1">{j.deal_label} → {j.to}</span>
                    <span className={`text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${statusColor(j.status)}`} data-testid={`fax-job-status-${j.id}`}>{j.status}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-[10px] text-[#6C6C64]">{(j.doc_keys || []).length} docs · {fmtTs(j.created_at)}{j.failure_reason ? ` · ${j.failure_reason}` : ""}</div>
                    <div className="flex items-center gap-2">
                      {j.certificate_filed_at && <span className="text-[9px] text-[#5fd3a8]" data-testid={`fax-cert-filed-${j.id}`}>✓ Filed</span>}
                      <button type="button" onClick={() => downloadCert(j.id)} data-testid={`fax-cert-btn-${j.id}`}
                        className="inline-flex items-center gap-1 text-[10px] text-[#C9A227] hover:text-[#E4C97A] border border-[#B8860B]/30 hover:border-[#B8860B]/60 rounded px-2 py-0.5">
                        <Download size={11} /> Certificate
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
